import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TranslationKeys } from '../../../../translations';
import { useBreadcrumb } from '../../../../components/Breadcrumbs';
import { useSnakeBar } from '../../../../hooks';
import { useStoreSelector } from '../../../../store';
import {
  useOwnerEmployersQuery,
  useDeletedConnectionsQuery,
  useRestoreConnectionMutation,
} from '../../../../hooks/api/admin';
import type { AdminOwnerEmployer, DeletedConnection } from '../../../../types';

export const useRestoreConnectionsPage = () => {
  const intl = useIntl();
  const { showSuccessSnakeBar, showErrorSnakeBar } = useSnakeBar();

  useBreadcrumb(TranslationKeys.menu_adminPanel_restoreConnections);

  const environmentOwnerId = useStoreSelector(state => state.AppReducer.environment.ownerId);

  const [employersSearchQuery, setEmployersSearchQuery] = React.useState('');
  const [selectedEmployer, setSelectedEmployer] = React.useState<AdminOwnerEmployer | null>(null);
  const [connectionToRestore, setConnectionToRestore] = React.useState<DeletedConnection | null>(null);
  const [employersPage, setEmployersPage] = React.useState(1);
  const [employersPerPage, setEmployersPerPage] = React.useState(10);
  const [connectionsPage, setConnectionsPage] = React.useState(1);
  const [connectionsPerPage, setConnectionsPerPage] = React.useState(10);

  const { data: employersData, isLoading: employersLoading } = useOwnerEmployersQuery({
    variables: {
      ownerId: environmentOwnerId,
      page: employersPage,
      itemsPerPage: employersPerPage,
      searchQuery: employersSearchQuery || undefined,
    },
    options: {
      enabled: !!environmentOwnerId,
    },
  });

  const { data: connectionsData, isLoading: connectionsLoading } = useDeletedConnectionsQuery({
    variables: {
      employerId: selectedEmployer?.employerId || '',
      page: connectionsPage,
      itemsPerPage: connectionsPerPage,
    },
    options: {
      enabled: !!selectedEmployer?.employerId,
    },
  });

  const { mutate: restoreConnection, isLoading: restoreLoading } = useRestoreConnectionMutation();

  const handleEmployersSearchChange = useCallback((value: string) => {
    setEmployersSearchQuery(value);
    setEmployersPage(1);
  }, []);

  const handleSelectEmployer = useCallback((employer: AdminOwnerEmployer) => {
    setSelectedEmployer(employer);
    setConnectionsPage(1);
  }, []);

  const handleBackToEmployers = useCallback(() => {
    setSelectedEmployer(null);
    setConnectionsPage(1);
  }, []);

  const handleRestoreClick = useCallback((connection: DeletedConnection) => {
    setConnectionToRestore(connection);
  }, []);

  const handleRestoreConfirm = useCallback(() => {
    if (connectionToRestore) {
      restoreConnection(
        { connectionId: connectionToRestore.connectionId },
        {
          onSuccess: () => {
            showSuccessSnakeBar({
              method: 'restoreConnection',
              message: intl.formatMessage({ id: TranslationKeys.tools_restoreConnections_restoreSuccess }),
            });
            setConnectionToRestore(null);
          },
          onError: () => {
            showErrorSnakeBar({
              method: 'restoreConnection',
              message: intl.formatMessage({ id: TranslationKeys.tools_restoreConnections_restoreError }),
            });
          },
        },
      );
    }
  }, [connectionToRestore, intl, restoreConnection, showErrorSnakeBar, showSuccessSnakeBar]);

  const handleRestoreCancel = useCallback(() => {
    setConnectionToRestore(null);
  }, []);

  const employersPagination = useMemo(
    () => ({
      page: employersPage,
      rowsPerPage: employersPerPage,
      totalCount: employersData?.totalItems || 0,
    }),
    [employersPage, employersPerPage, employersData?.totalItems],
  );

  const handleEmployersPaginationChange = useCallback(({ page, rowSize }: { page?: number; rowSize?: number }) => {
    if (page !== undefined) setEmployersPage(page);
    if (rowSize !== undefined) setEmployersPerPage(rowSize);
  }, []);

  const connectionsPagination = useMemo(
    () => ({
      page: connectionsPage,
      rowsPerPage: connectionsPerPage,
      totalCount: connectionsData?.totalItems || 0,
    }),
    [connectionsPage, connectionsPerPage, connectionsData?.totalItems],
  );

  const handleConnectionsPaginationChange = useCallback(({ page, rowSize }: { page?: number; rowSize?: number }) => {
    if (page !== undefined) setConnectionsPage(page);
    if (rowSize !== undefined) setConnectionsPerPage(rowSize);
  }, []);

  return {
    selectedEmployer,
    connectionToRestore,
    employersSearchQuery,
    employersData,
    employersLoading,
    connectionsData,
    connectionsLoading,
    restoreLoading,
    employersPagination,
    connectionsPagination,
    handleEmployersSearchChange,
    handleSelectEmployer,
    handleBackToEmployers,
    handleRestoreClick,
    handleRestoreConfirm,
    handleRestoreCancel,
    handleEmployersPaginationChange,
    handleConnectionsPaginationChange,
  };
};
